import { _ } from "../../core/polyglot/polyglot.js";
import { formatFullWithTwoDecimalsNoCurrency } from "../../core/helpers/currency-formatting-util.js";
import {
	formatNoDecimal,
	formatThousandWithoutAbbreviation,
} from "../../core/helpers/count-formatting-util.js";
import { select, theme } from "../../core/elements/index.js";
import { widget } from "@foretagsplatsen/widgetjs";
import accountRowVisibilityFactory from "../../models/reports/accountRowVisibilities/accountRowVisibilityFactory.js";
import ButtonGroupWidget from "../../core/widgets/ButtonGroupWidget/ButtonGroupWidget.js";
import ReportDisplaySettings from "../../models/reports/reports/ReportDisplaySettings.js";
import SwitchButton from "../../core/widgets/SwitchButton/SwitchButton.js";

/**
 * Widget displaying settings for a report.
 *
 * @param {reportDisplaySettings} [spec.displaySettings] - Display settings of the report
 * @param {boolean} [spec.showFormatterDropdown=true] - Allow the rendering of the formatter dropdown
 * @param {boolean} [spec.showExpansionLevel=true] - Allow the rendering of the expansion level widget
 * @param {boolean} [spec.showAccountVisibilitySwitch=true] - Allow the rendering of the account visibility switch
 * @param {boolean} [spec.showDebitPositiveSwitch=true] - Allow the rendering of the debit positive switch
 * @param {boolean} [spec.showPdfSettings=true] - Allow the rendering of the pdf settings section
 */
const displaySettingsWidget = widget.subclass((that, my) => {
	my.initialize = function ({
		displaySettings = new ReportDisplaySettings(),
		showFormatterDropdown = true,
		showExpansionLevel = true,
		showAccountVisibilitySwitch = true,
		showDebitPositiveSwitch = true,
		showPdfSettings = true,
	}) {
		my.super(...arguments);
		my.displaySettings = displaySettings;
		my.showFormatterDropdown = showFormatterDropdown;
		my.showExpansionLevel = showExpansionLevel;
		my.showAccountVisibilitySwitch = showAccountVisibilitySwitch;
		my.showDebitPositiveSwitch = showDebitPositiveSwitch;
		my.showPdfSettings = showPdfSettings;

		my.formatter = formatNoDecimal;
		my.expansionLevel = 2;
	};

	my.initializeSubwidgets = function () {
		my.super(...arguments);

		my.accountVisibilitySwitch = new ButtonGroupWidget({
			states: my.computeAccountVisibilityStates(),
		});

		my.showSummaryRowsSwitch = new SwitchButton({
			state: my.displaySettings.getShowSummaryRows(),
		});

		my.debitPositiveSwitch = new SwitchButton({
			state: !my.displaySettings.getDebitPositive(),
		});

		my.landscapeSwitch = new SwitchButton({
			state: my.displaySettings.getLandscape(),
		});

		my.showLogoSwitch = new SwitchButton({
			state: my.displaySettings.getShowLogo(),
		});
	};

	that.displaySettingsUpdated = my.events.createEvent();

	that.updateDisplaySettings = function () {
		my.displaySettings.setAccountVisibility(
			my.accountVisibilitySwitch.getActiveState().value,
		);

		my.displaySettings.setShowSummaryRows(
			my.showSummaryRowsSwitch.getState(),
		);

		my.displaySettings.setDebitPositive(!my.debitPositiveSwitch.getState());
		my.displaySettings.setFormatter(my.formatter);
		my.displaySettings.setLandscape(my.landscapeSwitch.getState());
		my.displaySettings.setShowLogo(my.showLogoSwitch.getState());
		my.displaySettings.setDefaultExpansionLevel(my.expansionLevel);

		that.displaySettingsUpdated.trigger(my.displaySettings);
	};

	that.getDisplaySettings = function () {
		return my.displaySettings;
	};

	that.setDisplaySettings = function (displaySettings) {
		my.displaySettings = displaySettings;
		that.refreshState();

		my.showSummaryRowsSwitch.setState(
			my.displaySettings.getShowSummaryRows(),
		);

		my.debitPositiveSwitch.setState(!my.displaySettings.getDebitPositive());
		my.formatter = my.displaySettings.getFormatter();
		my.landscapeSwitch.setState(my.displaySettings.getLandscape());
		my.showLogoSwitch.setState(my.displaySettings.getShowLogo());
		my.expansionLevel = my.displaySettings.getDefaultExpansionLevel();

		that.update();
	};

	that.refreshState = function () {
		my.accountVisibilitySwitch.setStates(
			my.computeAccountVisibilityStates(),
		);
	};

	my.computeAccountVisibilityStates = function () {
		return accountRowVisibilityFactory
			.allUserVisibleAccountVisibilities()
			.map((visibility) => ({
				label: visibility.getLabel(),
				value: visibility,
				active: my.displaySettings
					.getAccountVisibility()
					.matches(visibility),
			}));
	};

	that.renderContentOn = function (html) {
		theme(
			{ name: "dark-select", renderOn: html },
			html.div(
				{ klass: "setting-section" },
				html.label(_("General settings")),
				my.generalSettingsRenderer(),
			),
		);

		if (my.showPdfSettings) {
			my.renderPdfSettings(html);
		}
	};

	my.generalSettingsRenderer = function () {
		return (html) => {
			if (my.showFormatterDropdown) {
				my.renderFormatterDropdown(html);
			}
			if (my.showExpansionLevel) {
				my.renderExpansionLevel(html);
			}
			if (my.showAccountVisibilitySwitch) {
				my.renderAccountVisibilitySwitch(html);
			}
			if (my.showSummaryRowsSwitch) {
				my.renderShowSummaryRowsSwitch(html);
			}
			if (my.showDebitPositiveSwitch) {
				my.renderDebitPositiveSwitch(html);
			}
		};
	};

	my.renderFormatterDropdown = function (html) {
		html.div(
			{ klass: "setting-block" },
			_("Formatting"),
			select({
				items: [
					{
						label: _("Show decimals"),
						value: formatFullWithTwoDecimalsNoCurrency,
					},
					{
						label: _("No decimals"),
						value: formatNoDecimal,
					},
					{
						label: _("Shorten thousands"),
						value: formatThousandWithoutAbbreviation,
					},
				],
				selected: my.formatter,
				onChange: (formatter) => (my.formatter = formatter),
			}),
		);
	};

	my.renderExpansionLevel = function (html) {
		html.div(
			{ klass: "setting-block" },
			_("Level"),
			select({
				items: [
					{ value: 1, label: _("Level 1") },
					{ value: 2, label: _("Level 2") },
					{ value: 3, label: _("Level 3") },
					{ value: 4, label: _("Level 4") },
					{ value: Infinity, label: _("Expand all") },
				],
				selected: my.expansionLevel,
				onChange: (expansionLevel) =>
					(my.expansionLevel = expansionLevel),
			}),
		);
	};

	my.renderAccountVisibilitySwitch = function (html) {
		html.div(
			{ klass: "setting-block" },
			_("Show accounts"),
			my.accountVisibilitySwitch,
		);
	};

	my.renderShowSummaryRowsSwitch = function (html) {
		html.div(
			{ klass: "setting-block" },
			_("Summary rows"),
			my.showSummaryRowsSwitch,
		);
	};

	my.renderDebitPositiveSwitch = function (html) {
		html.div(
			{ klass: "setting-block" },
			_("Credit positive"),
			my.debitPositiveSwitch,
		);
	};

	my.renderPdfSettings = function (html) {
		html.div(
			{ klass: "setting-section" },
			html.label(_("PDF settings")),
			html.div(
				{ klass: "setting-block" },
				_("A4 Landscape"),
				my.landscapeSwitch,
			),
			html.div(
				{ klass: "setting-block" },
				_("Show logo"),
				my.showLogoSwitch,
			),
		);
	};
});

export default displaySettingsWidget;
